import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { useForgotPassword } from '@/features/auth/hooks/useAuth'
import { AuthLayout } from '@/layouts/AuthLayout'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z.object({
  email: z.email('Enter a valid email address'),
})

type FormValues = z.infer<typeof schema>

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const forgotPassword = useForgotPassword()
  const [apiError, setApiError] = useState<unknown>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  })

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    forgotPassword.mutate(values, {
      onSuccess: () => void navigate('/reset-password', { state: { email: values.email } }),
      onError: (error) => setApiError(error),
    })
  })

  return (
    <AuthLayout title="Forgot Password" subtitle="We'll email you a reset code">
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(apiError)}
        generalMessage={apiError ? getErrorMessage(apiError) : null}
      />
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <FormInput control={control} name="email" label="Email" type="email" required />
        <Button type="submit" className="w-100" loading={forgotPassword.isPending}>
          Send Reset Code
        </Button>
      </form>
      <div className="text-center mt-3">
        <p className="text-muted mb-0">
          <Link to="/login" className="text-primary">
            Back to Sign In
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}
